import { Grid, Menu, MenuItem, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import SelectionModal from "./selectionModal";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { ExpectedFileColumn } from "../../../app/models/requestModels/fileExpectedColumn";
import Loader from "../loader/Loader";

interface ValidationResult {
  validData: any[];
  invalidData: { data: any; error: string }[];
}
interface SelectedFileModalProps {
  closeSelectedFileModal: CallableFunction;
  onCompleted?: CallableFunction;
  expectedColumns: ExpectedFileColumn[];
  asyncSubmitOperation: any;
  selectedFile: File | null;
  acceptNumberAsZero?: boolean;
  acceptPercentageAsZero?: boolean;
}
const SelectedFileModal = ({
  closeSelectedFileModal,
  expectedColumns,
  asyncSubmitOperation,
  onCompleted,
  selectedFile,
  acceptNumberAsZero = false,
  acceptPercentageAsZero = false,
}: SelectedFileModalProps) => {
  const [isFileSelectionLoading, setIsFileSelectionLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [fileParsedHeader, setFileParsedHeader] = useState<string[]>([]);
  const [fileParsedData, setFileParsedData] = useState<any[][]>();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [selectedHeaders, setSelectedHeaders] = useState<
    Record<string, string | "No Header">
  >({});

  useEffect(() => {
    if (selectedFile) {
      const fileReader = new FileReader();
      fileReader.readAsBinaryString(selectedFile);
      fileReader.onload = (f) => {
        const data = f.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetname = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetname];
        const parsedData = XLSX.utils.sheet_to_json<any[]>(sheet, {
          header: 1,
        });
        if (parsedData.length > 1) {
          const sanitizedHeaders = parsedData[0].map((header) => {
            // Remove asterisks if attached mistakenly by user.
            return header.replace(/\*/g, "");
          });
          setFileParsedHeader(sanitizedHeaders);
          setFileParsedData(parsedData);
          prepareAutoSelection(sanitizedHeaders);
          setOpenModal(true);
        } else {
          toast.error("Either file doesn't have data or required headers!");
        }
      };
    }
    setIsFileSelectionLoading(false);
  }, [selectedFile]);

  useEffect(() => {
    const hasNoHeader = expectedColumns
      .filter((column) => column.mandatory)
      .some((column) => selectedHeaders[column.name] === "No Header");
    setDisableSubmit(hasNoHeader);
  }, [selectedHeaders]);

  const prepareAutoSelection = (headers: any[]) => {
    if (headers.length > 0) {
      const autoSelectedHeaders: Record<string, string | "No Header"> = {};
      expectedColumns.forEach((expectedColumn) => {
        const matchingHeader = headers.find(
          (header) =>
            header
              .replace(/[\s_-]/g, "")
              .trim()
              .toLowerCase() ===
            expectedColumn.name.replace(/\s/g, "").trim().toLowerCase()
        );
        autoSelectedHeaders[expectedColumn.name] =
          matchingHeader || "No Header";
      });
      // Update selectedHeaders state by auto selected values
      setSelectedHeaders(autoSelectedHeaders);
    }
  };

  const handleModalClose = (value: boolean) => {
    setOpenModal(false);
    closeSelectedFileModal();
  };

  const checkforColumnException = (column: ExpectedFileColumn) => {
    return column.mandatory && selectedHeaders[column.name] === "No Header";
  };

  const handleChangeColumnSelection = (
    selectedValue: string,
    columnName: string
  ) => {
    setSelectedHeaders((prevState) => ({
      ...prevState,
      [columnName]: selectedValue,
    }));
  };

  const prepareFileDataToJson = () => {
    if (fileParsedData && fileParsedHeader) {
      let newMappedData: any[] = [];
      let index = 0;
      fileParsedData.forEach((row, idx) => {
        if (idx > 0) {
          let obj: Record<string, number | string> = {};
          // Iterate over each expected standard column
          Object.entries(selectedHeaders).forEach(
            ([expectedColumn, fileColumnHeader]) => {
              const fileColumnIndex =
                fileParsedHeader.indexOf(fileColumnHeader);
              if (fileColumnIndex !== -1) {
                const objectKey = expectedColumns.find(
                  (col) => col.name === expectedColumn
                )?.camelCaseName;
                if (objectKey) obj[objectKey] = row[fileColumnIndex];
              }
            }
          );
          newMappedData[index++] = obj;
        }
      });
      return newMappedData;
    }
  };

  const handleAsyncSubmit = async () => {
    const mappedDataList = prepareFileDataToJson();
    const filteredDataResult = filterValidDataFromFile(mappedDataList);
    const validData = filteredDataResult.validData;
    const invalidData = filteredDataResult.invalidData;
    const stringifiedMappedDataList = JSON.parse(JSON.stringify(validData));
    const result = await asyncSubmitOperation(
      stringifiedMappedDataList,
      invalidData
    );
    handleModalClose(false);
    if (result && onCompleted) {
      onCompleted();
    }
  };

  const validateDataAgainstColumns = (
    data: any
  ): { isValid: boolean; error?: string } => {
    for (const column of expectedColumns) {
      const isMandatory = column.mandatory;
      let value = data[column.camelCaseName];
      if (isMandatory && !data.hasOwnProperty(column.camelCaseName)) {
        return {
          isValid: false,
          error: `Missing mandatory column '${column.name}'`,
        };
      }
      if (isMandatory) {
        if (acceptNumberAsZero && column.type === "number" && value === 0) {
          // number is zero do nothing
        } else if (
          acceptPercentageAsZero &&
          column.type === "percentage" &&
          value === 0
        ) {
          // percentage zero do nothing
        } else if (!value) {
          return {
            isValid: false,
            error: `Missing '${column.name}' value.`,
          };
        }
      }
      if (data.hasOwnProperty(column.camelCaseName) && value) {
        if (column.type === "date" && typeof value !== "number") {
          return {
            isValid: false,
            error: `Date format i.e MM/dd/yyyy required for '${column.name}'`,
          };
        }
        if (column.type === "date" && typeof value === "number") {
          const dateValue = XLSX.SSF.parse_date_code(value);
          let year = dateValue.y;
          let month = dateValue.m;
          let day = dateValue.d;
          value =
            year +
            "-" +
            (month < 10 ? "0" + month : month) +
            "-" +
            (day < 10 ? "0" + day : day);
          if (value === "1900-01-00") {
            return {
              isValid: false,
              error: `Date format i.e MM/dd/yyyy required for '${column.name}'`,
            };
          }
        }

        if (column.type === "string" && typeof value === "string") {
          value = value.trim();
        }
        if (column.type === "number" && typeof value === "string") {
          // Attempt to parse string to number
          const parsedValue = parseFloat(value);
          if (!isNaN(parsedValue)) {
            value = parsedValue;
          } else {
            return {
              isValid: false,
              error: `Number format required for '${column.name}'`,
            };
          }
        }
        if (column.type === "string" && typeof value !== "string") {
          return {
            isValid: false,
            error: `String format required for '${column.name}'`,
          };
        }
        if (column.type === "number" && typeof value !== "number") {
          return {
            isValid: false,
            error: `Number format required for '${column.name}'`,
          };
        }
        if (column.type === "percentage" && typeof value !== "number") {
          return {
            isValid: false,
            error: `Number(as percentage) format required for '${column.name}'`,
          };
        }
        if (column.regex && !column.regex.test(value)) {
          return {
            isValid: false,
            error: `Invalid format for '${column.name}'`,
          };
        }
        if (column.type.indexOf("[]") !== -1) {
          const arr = column.type.split(",");
          const type = arr[0];

          if (type === "number" && typeof value === "string") {
            const numArr = value
              .trim()
              .replace(/^,+|,+$/g, "")
              .split(",");
            const validNumFormatArr = numArr
              .map((g: any) => {
                const parsedValue = parseFloat(g);
                if (isNaN(parsedValue)) {
                  return {
                    isValid: false,
                    error: `Number array in string format value required for '${column.name}'`,
                  };
                }
              })
              .find((h: any) => h !== undefined);
            if (validNumFormatArr) {
              return validNumFormatArr;
            }
          } else if (type === "string" && typeof value === "string") {
          } else {
            return {
              isValid: false,
              error: `Number array in string format value required for '${column.name}'`,
            };
          }
        }
      }
    }
    return { isValid: true };
  };

  const filterValidDataFromFile = (
    mappedDataList: any[] | undefined
  ): ValidationResult => {
    const result: ValidationResult = { validData: [], invalidData: [] };
    if (!mappedDataList) return result;
    for (let data of mappedDataList) {
      const validation = validateDataAgainstColumns(data);
      if (validation.isValid) {
        for (const column of expectedColumns) {
          if (column.type.indexOf("[]") !== -1 && data[column.camelCaseName]) {
            let valueArray = data[column.camelCaseName]
              .trim()
              .replace(/^,+|,+$/g, "")
              .split(",")
              .map((val: string) => val.trim());
            const arr = column.type.split(",");
            const type = arr[0];
            if (type === "number") {
              data[column.camelCaseName] = valueArray.map((e: string) =>
                parseFloat(e)
              );
            } else {
              data[column.camelCaseName] = valueArray;
            }
          }
          if (column.type === "date" && data[column.camelCaseName]) {
            let dateValue = XLSX.SSF.parse_date_code(
              data[column.camelCaseName]
            );
            let year = dateValue.y;
            let month = dateValue.m;
            let day = dateValue.d;
            data[column.camelCaseName] =
              year +
              "-" +
              (month < 10 ? "0" + month : month) +
              "-" +
              (day < 10 ? "0" + day : day);
          }
          if (column.type === "percentage" && data[column.camelCaseName]) {
            // convert percentage value to decimal
            // we receive 0.1% as 0.001 in value
            // so we need to change 0.001 to 0.1
            data[column.camelCaseName] = data[column.camelCaseName] * 100;
          }
        }
        result.validData.push(data);
      } else {
        result.invalidData.push({
          data: data,
          error: validation.error || "Unknown error",
        });
      }
    }

    return result;
  };

  const renderMenuItems = (
    fileParsedHeader: string[],
    column: ExpectedFileColumn
  ) => {
    const normalizeFileColumnName = (str: string) =>
      str
        .replace(/[\s_-]/g, "")
        .trim()
        .toLowerCase();
    // Filtered and mapped items
    const menuItems = [
      <MenuItem
        key="no-header"
        sx={{ display: column.mandatory ? "none" : "block" }}
        value="No Header"
      >
        No Header Found
      </MenuItem>,

      // Dynamic menu items
      ...fileParsedHeader
        .filter(
          (e) =>
            normalizeFileColumnName(e) ===
              normalizeFileColumnName(column.name) ||
            !expectedColumns
              .map((ex) => normalizeFileColumnName(ex.name))
              .includes(normalizeFileColumnName(e))
        )
        .map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        )),
    ];
    return menuItems;
  };

  return (
    <>
      <Loader isLoading={isFileSelectionLoading} />
      <SelectionModal
        open={openModal}
        onClose={handleModalClose}
        title={"File Selection"}
        disableSubmit={disableSubmit}
        showCancelOption={true}
        asyncOperation={handleAsyncSubmit}
      >
        {expectedColumns.map((column, index) => (
          <Grid container spacing={2} key={index}>
            <Grid
              item
              sx={{
                display: "flex",
                mt: 1,
                justifyContent: {
                  xs: "flex-start",
                  sm: "end",
                  lg: "end",
                  md: "end",
                },
              }}
              xs={12}
              sm={5}
              md={4.5}
              lg={4.5}
            >
              <Typography sx={{ mt: 0.5, fontSize: 16, fontWeight: "18px" }}>
                {column.name} {column.mandatory && <b>*</b>}
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                display: "flex",
                mt: {
                  xs: -2,
                  lg: 1,
                  md: 1,
                  sm: 1,
                },
                justifyContent: {
                  xs: "center",
                  sm: "start",
                  lg: "start",
                  md: "start",
                },
              }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
            >
              {selectedHeaders[column.name] && (
                <TextField
                  select
                  size="small"
                  fullWidth
                  name={column.name}
                  error={checkforColumnException(column)}
                  value={selectedHeaders[column.name] || "No Header"}
                  onChange={(event) =>
                    handleChangeColumnSelection(event.target.value, column.name)
                  }
                >
                  {renderMenuItems(fileParsedHeader, column)}
                </TextField>
              )}
            </Grid>
          </Grid>
        ))}
      </SelectionModal>
    </>
  );
};

export default SelectedFileModal;
